<template>
  <div class="location-assignment">
    <Filters
      :filters="customerSampleAnalysesFilters"
      filter-key="customerSampleAnalyses"
      :initial-data="filtersStatus.customerSampleAnalyses"
      :loading="filtersLoading"
    />

    <ViewActionButtons :buttons="actionButtons" />

    <TableWrapper
      :table-columns="customerSampleAnalysesColumns"
      table-key="customerSampleAnalyses"
      row-key="analysis.id"
      :enable-selection="true"
      :massive-actions-options="bulkActions"
      @massiveAction="bulkApply"
    />

    <DeafultLocationSettingsModal
      v-if="openDefaultLocationSettings"
      @dialogClose="handleDialogClose"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import {
    customerSampleAnalysesColumns,
    customerSampleAnalysesFilters,
  } from '@/config';
  import { customerSampleAnalysesApi, locationsManagementApi } from '@/api';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import axios from 'axios';

  import TableWrapper from '@/components/TableWrapper.vue';
  import Filters from '@/components/Filters.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import DeafultLocationSettingsModal from './DeafultLocationSettingsModal.vue';

  export default {
    name: 'CustomerSampleAnalysesIndex',
    components: {
      TableWrapper,
      Filters,
      ViewActionButtons,
      DeafultLocationSettingsModal,
    },
    mixins: [abortControllerMixin, filterLoadingMixin],
    data() {
      return {
        customerSampleAnalysesColumns,
        customerSampleAnalysesFilters,
        bulkActions: [],
        actionButtons: [
          {
            text: this.$t('customerSampleAnalyses.deafultLocationSettings'),
            type: 'primary',
            permissions: ['sampleAnalysisLocationManage'],
            action: () => {
              this.openDefaultLocationSettings = true;
            },
          },
        ],
        openDefaultLocationSettings: false,
        hasLocation: [{ key: false }],
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus']),
    },
    async created() {
      const query = { ...this.$route.query };
      if (!query.filterData) {
        query.filterData = JSON.stringify({
          hasLocation: [
            {
              key: false,
              label: this.$t('customerSampleAnalyses.hasLocation.toBeAssigned'),
            },
          ],
        });
        query.filterKey = 'customerSampleAnalyses';
      }
      this.parseQueryStringStatus(query);
      this.tableRows(query);
      await this.locationsList();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows(to.query);
      next();
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
      ]),
      async tableRows() {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;
        try {
          const customerSampleAnalysesIndex =
            await customerSampleAnalysesApi.index(
              this.tableFilters('customerSampleAnalyses'),
              {
                ...this.filtersStatus.customerSampleAnalyses,
              },
              { signal }
            );
          const customerSampleAnalysesCount =
            await customerSampleAnalysesApi.count(
              this.filtersStatus.customerSampleAnalyses,
              { signal }
            );
          this.updateTableRows({
            tableKey: 'customerSampleAnalyses',
            rows: customerSampleAnalysesIndex.data,
          });
          this.updateTableCount({
            tableKey: 'customerSampleAnalyses',
            count: customerSampleAnalysesCount.data.count,
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      async locationsList() {
        const response = await locationsManagementApi.autocompleteFetch(
          'name',
          ''
        );
        this.bulkActions = response.data.map((c) => ({
          label: c.name,
          value: c.id,
        }));
      },
      async bulkApply(locationId, selectedRows) {
        try {
          const sampleAnalysisIds = selectedRows.map((row) => row.analysis.id);
          await customerSampleAnalysesApi.bulkUpdate(
            sampleAnalysisIds,
            locationId
          );
          this.tableRows();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('customerSampleAnalyses.update.bulkSuccess'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      handleDialogClose(status) {
        this.openDefaultLocationSettings = false;
        if (status.success) this.tableRows();
      },
    },
  };
</script>
